import {Component, HostListener, OnInit} from '@angular/core';
import * as dayjs from 'dayjs';
import {Router} from '@angular/router';
import {HttpClient} from '@angular/common/http';
import {MatDialog} from '@angular/material/dialog';
import {LocalstorageService} from '../../../@services/localstorage.service';
import {NotificationService} from '../../../@services/notification.service';
import {environment} from '../../../../environments/environment';
import {WastageDialogComponent} from '../wastage-dialog/wastage-dialog.component';
import {UserShiftService} from '../../../@services/user-shift.service';

@Component({
  selector: 'app-wastage-list',
  templateUrl: './wastage-list.component.html',
  styleUrls: ['./wastage-list.component.scss'],
})
export class WastageListComponent implements OnInit {
  title = 'Wastage - Current Shift';
  isMobile: boolean = false;
  currentShiftId: number | null = null;
  currentShiftStartDisplay: string = '-';
  hasActiveShift = false;

  wastages: any[] = [];
  totalAmount = 0;
  totalQuantity = 0;

  get totalRecords(): number {
    return this.wastages.length;
  }

  constructor(
    private http: HttpClient,
    private router: Router,
    private ls: LocalstorageService,
    private ns: NotificationService,
    private dialog: MatDialog,
    private userShiftService: UserShiftService,
  ) {
    this.checkIsMobile();
  }

  ngOnInit(): void {
    const merchant = this.ls.getMerchant() as any;
    if (merchant?.wastageConfig?.isAllowWastage !== true) {
      this.ns.showNotification({message: 'Wastage feature is disabled for this merchant.'});
      this.router.navigate(['/']);
      return;
    }

    const currentShift = this.userShiftService.getUserShift();
    this.currentShiftId = currentShift?.id || null;
    this.currentShiftStartDisplay = currentShift?.start
      ? dayjs(currentShift.start).format('DD/MM/YY HH:mm')
      : '-';

    if (!this.currentShiftId) {
      this.hasActiveShift = false;
      this.ns.showNotification({message: 'No active shift found. Please start shift first.'});
      this.router.navigate(['/']);
      return;
    }

    this.hasActiveShift = true;
    this.load();
  }

  @HostListener('window:resize', ['$event'])
  onResize(event) {
    this.checkIsMobile();
  }

  private checkIsMobile() {
    this.isMobile = window.innerWidth < 768;
  }

  load() {
    const dialogRef = this.ns.showNotification({
      message: 'Loading wastage...',
      icon: 'fa fa-sync',
      manualClose: true,
    });

    const merchant = this.ls.getMerchant();
    this.http.post<any>(`${environment.apiUrl}/api/wastage/getPagedWastage`, {
      merchantId: merchant.id,
      shiftId: this.currentShiftId,
      pageRequest: {
        page: 0,
        size: 999,
      },
      search: '',
    }).subscribe(
      (res) => {
        dialogRef.close();
        this.wastages = (res?.content || []).map((row) => ({
          ...row,
          occurredAtDisplay: row.occurredAt ? dayjs(row.occurredAt).format('DD/MM/YY HH:mm') : '-',
          sourceDisplay: (row.source || '-').toString().replace(/_/g, ' '),
          editableInPos: this.currentShiftId != null && row?.shiftId === this.currentShiftId,
        }));
        this.calculateTotals();
      },
      (error) => {
        dialogRef.close();
        this.ns.showNotification({message: error?.error?.message || 'Failed to load wastage records'});
      }
    );
  }

  private calculateTotals() {
    this.totalAmount = this.wastages.reduce((sum, row) => sum + Number(row.totalAmount || 0), 0);
    this.totalQuantity = this.wastages.reduce((sum, row) => sum + Number(row.quantity || 0), 0);
  }

  addWastage() {
    this.openWastageDialog();
  }

  private openWastageDialog(wastage?: any) {
    this.dialog.open(WastageDialogComponent, {
      width: this.isMobile ? '96vw' : '760px',
      maxWidth: '96vw',
      autoFocus: false,
      data: {
        shiftId: this.currentShiftId,
        wastage,
      },
    }).afterClosed().subscribe((res) => {
      if (res) {
        this.load();
      }
    });
  }

  trackByWastageId(_: number, row: any): number {
    return row?.id;
  }

  editWastage(row: any) {
    if (!row?.editableInPos) {
      this.ns.showNotification({message: 'Only current shift wastage can be edited in POS.'});
      return;
    }

    const loading = this.ns.showNotification({
      message: 'Loading wastage detail...',
      icon: 'fa fa-sync',
      manualClose: true,
    });
    this.http.post<any>(`${environment.apiUrl}/api/wastage/getWastage`, {id: row.id}).subscribe(
      (res) => {
        loading.close();
        this.openWastageDialog(res || row);
      },
      (error) => {
        loading.close();
        this.ns.showNotification({message: error?.error?.message || 'Failed to load wastage detail'});
      }
    );
  }

  deleteWastage(row: any) {
    if (!row?.editableInPos) {
      this.ns.showNotification({message: 'Only current shift wastage can be deleted in POS.'});
      return;
    }

    const confirmed = window.confirm(`Delete wastage record for "${row?.itemName || 'item'}"?`);
    if (!confirmed) {
      return;
    }

    const loading = this.ns.showNotification({
      message: 'Deleting wastage...',
      icon: 'fa fa-sync',
      manualClose: true,
    });

    const merchant = this.ls.getMerchant();
    this.http.post<any>(`${environment.apiUrl}/api/wastage/deleteWastage`, {
      id: row.id,
      merchantId: merchant?.id,
    }).subscribe(
      () => {
        loading.close();
        this.ns.showNotification({message: 'Wastage deleted successfully'});
        this.load();
      },
      (error) => {
        loading.close();
        this.ns.showNotification({message: error?.error?.message || 'Failed to delete wastage'});
      }
    );
  }
}
